/**
 * Pagina de import în lot (registry-import).
 *
 * Permite selecția unui folder local, filtrarea fișierelor, upload,
 * vizualizarea loturilor și filtrarea item-urilor pentru validare.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Radio, Input, Table, Tag, Space, Divider,
  Progress, Alert, message, Select, Spin, Tooltip,
} from 'antd';
import {
  UploadOutlined, FolderOpenOutlined, ReloadOutlined,
  WarningOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import type { ProgramType } from '../../api/registryImportApi';
import { registryImportApi, type BatchSummary, type ItemSummary } from '../../api/registryImportApi';

type FilterMode = 'all' | 'needs_review' | 'blocking' | 'approved' | 'rejected' | 'failed';

// Fișiere/foldere de sistem sau artefacte de analiză (ca în backend): verificate pe
// fiecare segment al căii relative.
const IGNORED_SEGMENTS = [/^__MACOSX$/, /^\.DS_Store$/, /^\._/, /^_pages$/, /^_checkbox-analysis$/];

const shouldIgnoreFile = (path: string): boolean =>
  // doar PDF-urile sunt documente-sursă (note .md, .json, imagini de lucru se ignoră)
  !/\.pdf$/i.test(path) || path.split('/').some((segment) => IGNORED_SEGMENTS.some((re) => re.test(segment)));

interface FolderSelection {
  files: File[];
  paths: string[];
  folderCount: number;
  fileCount: number;
}

const BatchImportPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const patientsRef = useRef<HTMLDivElement>(null);

  const [programType, setProgramType] = useState<ProgramType>('DERMATO');
  const [label, setLabel] = useState<string>('');
  const [selection, setSelection] = useState<FolderSelection | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  const [items, setItems] = useState<ItemSummary[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  const [pollingBatchId, setPollingBatchId] = useState<string | null>(null);

  // Încarcă loturile la mount
  useEffect(() => {
    loadBatches();
  }, []);

  // Polling pentru lotul selectat (dacă există documente în procesare)
  useEffect(() => {
    if (!selectedBatchId) {
      setPollingBatchId(null);
      return;
    }

    const batch = batches.find((b) => b.id === selectedBatchId);
    if (!batch) return;

    const inProgress = batch.itemStats.QUEUED + batch.itemStats.PROCESSING;
    if (inProgress > 0) {
      setPollingBatchId(selectedBatchId);
      const timer = setInterval(() => {
        loadBatch(selectedBatchId);
        loadItems(selectedBatchId);
      }, 3000);
      return () => clearInterval(timer);
    } else {
      setPollingBatchId(null);
    }
  }, [selectedBatchId, batches]);

  const loadBatches = async () => {
    setLoadingBatches(true);
    try {
      const data = await registryImportApi.listBatches();
      setBatches(data);
    } catch (error: any) {
      console.error('Failed to load batches:', error);
    } finally {
      setLoadingBatches(false);
    }
  };

  const loadBatch = async (batchId: string) => {
    try {
      const batch = await registryImportApi.getBatch(batchId);
      setBatches((prev) => prev.map((b) => (b.id === batchId ? batch : b)));
    } catch (error: any) {
      console.error('Failed to load batch:', error);
    }
  };

  const loadItems = async (batchId: string): Promise<ItemSummary[] | undefined> => {
    setLoadingItems(true);
    try {
      const data = await registryImportApi.listItems(batchId);
      setItems(data);
      return data;
    } catch (error: any) {
      console.error('Failed to load items:', error);
      return undefined;
    } finally {
      setLoadingItems(false);
    }
  };

  const handleFolderSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    const filesArray = Array.from(fileList);
    const filtered: { file: File; path: string }[] = [];
    const folders = new Set<string>();

    for (const file of filesArray) {
      const relativePath = (file as any).webkitRelativePath || file.name;

      if (shouldIgnoreFile(relativePath)) {
        continue;
      }

      filtered.push({ file, path: relativePath });

      // Pacientul = folderul care conține fișierul (selecția poate porni de la un
      // folder-rădăcină, ex. „PIELE RAPORTARE 1/PACIENT/fisier.pdf”)
      const parts = relativePath.split('/');
      if (parts.length > 1) {
        folders.add(parts[parts.length - 2]);
      }
    }

    setSelection({
      files: filtered.map((f) => f.file),
      paths: filtered.map((f) => f.path),
      folderCount: folders.size,
      fileCount: filtered.length,
    });

    // Reset input pentru a permite reselectarea aceluiași folder
    event.target.value = '';
  };

  const handleUpload = async () => {
    if (!selection || selection.files.length === 0) {
      message.warning('Selectați un folder cu documente');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulare progres (axios nu expune progres pe multipart corect în toate browserele)
      const progressTimer = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const result = await registryImportApi.uploadBatch(
        selection.files,
        selection.paths,
        programType,
        label || undefined
      );

      clearInterval(progressTimer);
      setUploadProgress(100);

      message.success(`Lot încărcat: ${result.totalItems} pacienți, ${result.totalDocuments} documente`);
      setSelection(null);
      setLabel('');

      // Reîncarcă loturile și selectează lotul nou
      await loadBatches();
      setSelectedBatchId(result.batchId);
      await loadItems(result.batchId);
      // lista pacienților noului lot (se actualizează singură cât durează procesarea)
      setTimeout(() => patientsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (error: any) {
      message.error('Eroare la încărcare: ' + (error.message || 'eroare necunoscută'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleBatchSelect = async (batchId: string) => {
    setSelectedBatchId(batchId);
    setFilterMode('all');
    const batch = batches.find((b) => b.id === batchId);
    const loaded = await loadItems(batchId);
    // un singur pacient de verificat → direct la ecranul de verificare
    const toReview = (loaded ?? []).filter((i) => i.status === 'NEEDS_REVIEW');
    if (batch && batch.totalItems === 1 && toReview.length === 1) {
      navigate(`/registry-import/items/${toReview[0].id}`);
      return;
    }
    // altfel lista pacienților, adusă în ecran (altfel pare că butonul nu face nimic)
    setTimeout(() => patientsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleRefreshBatch = () => {
    if (selectedBatchId) {
      loadBatch(selectedBatchId);
      loadItems(selectedBatchId);
    }
  };

  const filteredItems = items.filter((item) => {
    if (filterMode === 'all') return true;
    if (filterMode === 'needs_review') return item.status === 'NEEDS_REVIEW';
    if (filterMode === 'blocking') return item.blockingAlerts > 0;
    if (filterMode === 'approved') return item.status === 'APPROVED';
    if (filterMode === 'rejected') return item.status === 'REJECTED';
    if (filterMode === 'failed') return item.status === 'FAILED';
    return true;
  });

  const selectedBatch = batches.find((b) => b.id === selectedBatchId);

  const batchColumns = [
    {
      title: 'Etichetă',
      dataIndex: 'label',
      key: 'label',
      render: (label: string | null) => label || '—',
    },
    {
      title: 'Program',
      dataIndex: 'programType',
      key: 'programType',
      render: (type: ProgramType) => (
        <Tag color={type === 'DERMATO' ? 'blue' : 'green'}>{type}</Tag>
      ),
    },
    {
      title: 'Încărcat',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      render: (date: string) => new Date(date).toLocaleString('ro-RO'),
    },
    {
      title: 'Pacienți',
      dataIndex: 'totalItems',
      key: 'totalItems',
    },
    {
      title: 'Documente',
      dataIndex: 'totalDocuments',
      key: 'totalDocuments',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, batch: BatchSummary) => {
        const { itemStats } = batch;
        const total = batch.totalItems;
        const processing = itemStats.QUEUED + itemStats.PROCESSING;
        const needsReview = itemStats.NEEDS_REVIEW;
        const approved = itemStats.APPROVED;

        if (processing > 0) {
          return (
            <Space>
              <Spin size="small" />
              <span>Procesare {processing}/{total}</span>
            </Space>
          );
        }

        return (
          <Space>
            {needsReview > 0 && <Tag color="orange">{needsReview} de validat</Tag>}
            {approved > 0 && <Tag color="green">{approved} aprobate</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      render: (_: any, batch: BatchSummary) => (
        <Button
          type={batch.itemStats.NEEDS_REVIEW > 0 ? 'primary' : 'default'}
          onClick={() => void handleBatchSelect(batch.id)}
        >
          {batch.itemStats.NEEDS_REVIEW > 0 ? `Verifică pacienții (${batch.itemStats.NEEDS_REVIEW}) →` : 'Vezi pacienții'}
        </Button>
      ),
    },
  ];

  const itemColumns = [
    {
      title: 'Pacient (folder)',
      dataIndex: 'folderName',
      key: 'folderName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const meta: Record<string, { color: string; label: string }> = {
          QUEUED: { color: 'default', label: 'în așteptare' },
          PROCESSING: { color: 'blue', label: 'în procesare' },
          NEEDS_REVIEW: { color: 'orange', label: 'de verificat' },
          APPROVED: { color: 'green', label: 'aprobat — în registru' },
          REJECTED: { color: 'red', label: 'respins' },
          FAILED: { color: 'volcano', label: 'eșuat' },
        };
        return <Tag color={meta[status]?.color || 'default'}>{meta[status]?.label ?? status}</Tag>;
      },
    },
    {
      title: 'Documente',
      dataIndex: 'documentCount',
      key: 'documentCount',
    },
    {
      title: 'Alerte',
      key: 'alerts',
      render: (_: any, item: ItemSummary) => (
        <Space>
          {item.blockingAlerts > 0 && (
            <Tooltip title="Alerte blocante">
              <Tag color="red" icon={<WarningOutlined />}>{item.blockingAlerts}</Tag>
            </Tooltip>
          )}
          {item.infoAlerts > 0 && (
            <Tooltip title="Alerte informative">
              <Tag color="blue" icon={<InfoCircleOutlined />}>{item.infoAlerts}</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      render: (_: any, item: ItemSummary) => (
        <Button
          type={item.status === 'NEEDS_REVIEW' ? 'primary' : 'default'}
          onClick={() => navigate(`/registry-import/items/${item.id}`)}
        >
          {item.status === 'NEEDS_REVIEW' ? 'Verifică și aprobă →' : 'Vezi'}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 8 }}>Import în lot</h1>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="Cum funcționează"
        description={
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            <li><b>Încarci</b> un folder (un subfolder = un pacient, cu PDF-urile lui). Sistemul citește documentele.</li>
            <li>În „Loturi încărcate” apeși <b>Verifică pacienții</b>, apoi <b>Verifică și aprobă</b> la fiecare pacient.</li>
            <li>Compari datele extrase cu PDF-ul, corectezi ce e greșit și apeși <b>Aprobă</b>: pacientul intră în registru.</li>
          </ol>
        }
      />

      {/* Secțiunea de upload */}
      <Card title="Încărcare pachet nou" style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Tip program *
            </label>
            <Radio.Group
              value={programType}
              onChange={(e) => setProgramType(e.target.value)}
              options={[
                { label: 'Dermato (cancer piele)', value: 'DERMATO' },
                { label: 'Hepato (cancer ficat)', value: 'HEPATO' },
              ]}
              optionType="button"
              buttonStyle="solid"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Etichetă lot (opțional)
            </label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ex: Import feb 2026, Campanie screening, etc."
              style={{ maxWidth: 400 }}
            />
          </div>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              // @ts-ignore - webkitdirectory nu e în tipurile standard
              webkitdirectory=""
              multiple
              style={{ display: 'none' }}
              onChange={handleFolderSelect}
            />
            <Button
              icon={<FolderOpenOutlined />}
              onClick={() => fileInputRef.current?.click()}
              size="large"
            >
              Selectează folder
            </Button>
          </div>

          {selection && (
            <Alert
              type="info"
              message={`Selectat: ${selection.folderCount} pacienți / ${selection.fileCount} documente`}
              description="Se încarcă doar fișierele PDF; restul (note .md/.json, fișiere de sistem __MACOSX, .DS_Store) se ignoră automat."
            />
          )}

          {uploading && (
            <div>
              <Progress percent={uploadProgress} status="active" />
              <p style={{ marginTop: 8, color: '#666' }}>
                Se încarcă și procesează documentele... (poate dura câteva minute)
              </p>
            </div>
          )}

          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleUpload}
            loading={uploading}
            disabled={!selection || selection.files.length === 0}
            size="large"
          >
            {uploading ? 'Se încarcă...' : 'Încarcă pachetul'}
          </Button>
        </Space>
      </Card>

      <Divider />

      {/* Lista loturilor */}
      <Card
        title="Loturi încărcate"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={loadBatches}
            loading={loadingBatches}
          >
            Reîmprospătează
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          dataSource={batches}
          columns={batchColumns}
          rowKey="id"
          loading={loadingBatches}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Niciun lot încărcat' }}
        />
      </Card>

      {/* Detalii lot selectat */}
      <div ref={patientsRef} />
      {selectedBatch && (
        <Card
          title={
            <Space>
              <span>Pacienții din lotul {selectedBatch.label || `din ${new Date(selectedBatch.uploadedAt).toLocaleString('ro-RO')}`}</span>
              {pollingBatchId === selectedBatch.id && (
                <Tag color="blue" icon={<Spin size="small" />}>Actualizare automată</Tag>
              )}
            </Space>
          }
          extra={
            <Space>
              <Select
                value={filterMode}
                onChange={(value) => setFilterMode(value)}
                style={{ width: 200 }}
                options={[
                  { label: 'Toți', value: 'all' },
                  { label: 'De validat', value: 'needs_review' },
                  { label: 'Cu alerte blocante', value: 'blocking' },
                  { label: 'Aprobați', value: 'approved' },
                  { label: 'Respinși', value: 'rejected' },
                  { label: 'Eșuați', value: 'failed' },
                ]}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefreshBatch}
                loading={loadingItems}
              >
                Reîmprospătează
              </Button>
            </Space>
          }
        >
          <Table
            dataSource={filteredItems}
            columns={itemColumns}
            rowKey="id"
            loading={loadingItems}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: 'Niciun pacient în acest lot' }}
          />
        </Card>
      )}
    </div>
  );
};

export default BatchImportPage;
